import { memo, useEffect } from "react";
import { Group } from "@mantine/core";
import Textarea from "src/components/Textarea/Textarea";
import ImageUpload from "src/components/ImageUpload";
import {
  CreateLocationServiceInput,
  GenericConnectInput,
  useDeleteLocationServiceMutation,
  useGetRoomNamesQuery,
  useUpdateLocationServiceMutation,
} from "src/generated/graphql";
import { useForm, yupResolver } from "@mantine/form";
import { number, object, string } from "yup";
import NumberInput from "src/components/NumberInput/NumberInput";
import Switch from "src/components/Switch/Switch";
import { IPropsLocationService } from "./LocationService";
import { useDisclosure } from "@mantine/hooks";
import FormCard from "src/components/FormCard";
import { MESSAGES } from "src/utils/constants";
import MultiSelect from "src/components/MultiSelect";

export type IFormLocationService = Omit<
  CreateLocationServiceInput,
  "location" | "organizationService" | "rooms"
> & {
  location: string;
  organizationService?: GenericConnectInput;
  rooms: string[];
};

export const locationServiceInitialValues = {
  cost: Number(),
  description: "",
  imgUrl: "",
  isActive: true,
  isPrepay: true,
  location: "",
  rooms: [],
};

export const locationServiceFormSchema = object().shape({
  cost: number().required("Cost is required"),
  description: string().max(200).notRequired(),
  location: string().required("Location is required"),
});

export interface IProps {
  detail: IPropsLocationService["locationServices"][0];
  handleGetOrganizationService: () => void;
  visibleCards: string[];
  setvisibleCards: (e: string[]) => void;
  isAllowWrite: boolean;
}

const LocationServiceDetail = ({
  detail,
  handleGetOrganizationService,
  visibleCards,
  setvisibleCards,
  isAllowWrite,
}: IProps) => {
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const isVisible = visibleCards.includes(detail.id);

  const form = useForm<IFormLocationService>({
    initialValues: locationServiceInitialValues,
    validate: yupResolver(locationServiceFormSchema),
  });

  const { data: roomNames } = useGetRoomNamesQuery({
    variables: { locationId: form.getInputProps("location").value || "" },
    skip: !form.getInputProps("location").value,
  });

  const getSetValues = () => {
    if (detail) {
      const { id, __typename, location, rooms, ...rest } = detail;
      const obj = {
        ...rest,
        location: location.id,
        rooms: rooms.map((e) => e.id),
      };
      form.setValues(obj);
      form.resetDirty(obj);
    }
  };

  useEffect(() => {
    getSetValues();
  }, [detail]);

  const [updateLocationServiceService, { loading: updateLSLoading }] =
    useUpdateLocationServiceMutation({
      onCompleted: (data) => {
        toggleFormActive();
        handleGetOrganizationService();
        form.resetDirty();
        setvisibleCards(
          visibleCards.filter((e) => e !== data.updateLocationService.id)
        );
      },
      refetchQueries: ["GetOrganizationService"],
    });

  const [deleteLocationService, { loading: deleteLSLoading }] =
    useDeleteLocationServiceMutation({
      onCompleted: () => {
        handleGetOrganizationService();
      },
    });

  const handleSubmit = async (values: IFormLocationService) => {
    const { location, organizationService, rooms, ...rest } = values;
    updateLocationServiceService({
      variables: {
        data: {
          ...rest,
          rooms: rooms.map((e) => ({ id: e })),
        },
        id: detail?.id,
      },
    });
  };

  const handleDelete = () => {
    deleteLocationService({ variables: { id: detail.id } });
  };

  const handleEdit = () => {
    toggleFormActive();
    !isVisible && setvisibleCards([...visibleCards, detail.id]);
  };

  const handleClick = () => {
    if (isVisible) {
      if (!isFormActive) {
        getSetValues();
        toggleFormActive();
      }
      setvisibleCards(visibleCards.filter((e) => e !== detail.id));
    } else {
      setvisibleCards([...visibleCards, detail.id]);
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <FormCard
      title={detail.location.name}
      loading={updateLSLoading || deleteLSLoading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleClick}
      onDelete={isAllowWrite ? handleDelete : undefined}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleSubmit(values))}
      mt={"md"}
      isFormActive={isFormActive}
      startImage={isVisible ? "" : detail.imgUrl || "NA"}
      onHeaderClick={handleClick}
    >
      {isVisible && (
        <>
          <Group position="apart" mt="sm">
            <ImageUpload
              onReadSuccess={(b64) => form.setFieldValue("imgUrl", b64)}
              src={form.values.imgUrl || ""}
              alt="LocationService Logo"
              height={form.values.imgUrl ? "85px" : "60px"}
              maxWidth={form.values.imgUrl ? "85px" : "80px"}
              disabled={isFormActive}
              hideInnerText={true}
              iconSize={25}
            />
            <NumberInput
              withAsterisk
              precision={2}
              label="Cost"
              {...form.getInputProps("cost")}
              disabled={isFormActive}
            />
            <Switch
              labelPosition="left"
              label="Pre-Paid"
              checked={!!form.getInputProps("isPrepay")?.value}
              {...form.getInputProps("isPrepay")}
              disabled={isFormActive}
            />

            <Switch
              labelPosition="left"
              label="Active"
              checked={!!form.getInputProps("isActive")?.value}
              {...form.getInputProps("isActive")}
              disabled={isFormActive}
            />
          </Group>

          <MultiSelect
            data={
              roomNames?.roomMany?.map((val) => ({
                value: val.id,
                label: val.name,
              })) || []
            }
            label="Select Rooms"
            {...form.getInputProps("rooms")}
            styles={{ root: { width: "100%" } }}
            disabled={isFormActive}
          />
          <Textarea
            disabled={isFormActive}
            label="Description"
            {...form.getInputProps("description")}
          />
        </>
      )}
    </FormCard>
  );
};

export default memo(LocationServiceDetail);
